import React from "react";
import { Layers3 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

const num = (v) => Number(v) || 0;

// Floor-wise assessment view: property type + usage + areas per floor with totals.
// Renders nothing when the property has no saved floor assessment.
export const FloorAssessment = ({ floors }) => {
  if (!Array.isArray(floors) || floors.length === 0) return null;
  const totals = floors.reduce((t, f) => ({
    plot: t.plot + num(f.plot_area_sqft),
    open: t.open + num(f.open_area_sqft),
    constructed: t.constructed + num(f.constructed_area_sqft ?? f.area_sqft),
    carpet: t.carpet + num(f.carpet_area_sqft),
  }), { plot: 0, open: 0, constructed: 0, carpet: 0 });

  return (
    <Card className="shadow-sm" data-testid="floor-assessment-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Layers3 className="h-4 w-4" /> Floor-wise assessment
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto -mx-1 px-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Floor</TableHead>
                <TableHead className="text-xs">House No</TableHead>
                <TableHead className="text-xs">Property type</TableHead>
                <TableHead className="text-xs">Construction</TableHead>
                <TableHead className="text-xs">Usage / Sub-usage</TableHead>
                <TableHead className="text-xs text-right">Plot</TableHead>
                <TableHead className="text-xs text-right">Open</TableHead>
                <TableHead className="text-xs text-right">Constructed</TableHead>
                <TableHead className="text-xs text-right">Carpet</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {floors.map((f, i) => (
                <TableRow key={i} data-testid={`floor-assessment-row-${i}`}>
                  <TableCell className="text-xs font-medium whitespace-nowrap">{f.floor_label || `Floor ${i + 1}`}</TableCell>
                  <TableCell className="text-xs">{f.house_no || "—"}</TableCell>
                  <TableCell className="text-xs">{f.property_type_name || "—"}</TableCell>
                  <TableCell className="text-xs">{f.const_type_name || "—"}</TableCell>
                  <TableCell className="text-xs">
                    {f.usage_type_name || "—"}
                    {f.sub_usage_name ? <span className="text-muted-foreground"> / {f.sub_usage_name}</span> : ""}
                  </TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{num(f.plot_area_sqft)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{num(f.open_area_sqft)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{num(f.constructed_area_sqft ?? f.area_sqft)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{num(f.carpet_area_sqft)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/60 font-semibold" data-testid="floor-assessment-total-row">
                <TableCell className="text-xs" colSpan={5}>Total ({floors.length} floor{floors.length > 1 ? "s" : ""})</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{totals.plot}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{totals.open}</TableCell>
                <TableCell className="text-xs text-right tabular-nums" data-testid="floor-assessment-total-constructed">{totals.constructed}</TableCell>
                <TableCell className="text-xs text-right tabular-nums" data-testid="floor-assessment-total-carpet">{totals.carpet}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">All areas in sq.ft. Carpet area is assessed at 80% of constructed area.</p>
      </CardContent>
    </Card>
  );
};
